import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Search, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import api from "@/lib/api";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_helpdesk-hub-53/artifacts/dtuif3s8_NEWGamerLogo.jpg";

export default function TicketsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [selectedTicket, setSelectedTicket] = useState(null);

  const status = searchParams.get("status");
  const priority = searchParams.get("priority");
  const category = searchParams.get("category");
  const assignedTo = searchParams.get("assigned_to");
  const grade = searchParams.get("grade");

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 30 };
      if (status) params.status = status;
      if (priority) params.priority = priority;
      if (category) params.category = category;
      if (assignedTo) params.assigned_to = assignedTo;
      if (grade) params.grade = grade;
      if (search) params.search = search;

      const res = await api.getTickets(params);
      setTickets(res.data.tickets);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (e) {
      console.error("Failed to fetch tickets", e);
    } finally {
      setLoading(false);
    }
  }, [page, status, priority, category, assignedTo, grade, search]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const activeFilters = [];
  if (status) activeFilters.push({ label: `Status: ${status}`, key: 'status' });
  if (priority) activeFilters.push({ label: `Priority: ${priority}`, key: 'priority' });
  if (category) activeFilters.push({ label: `Category: ${category}`, key: 'category' });
  if (assignedTo) activeFilters.push({ label: `Assigned: ${assignedTo}`, key: 'assigned_to' });
  if (grade) activeFilters.push({ label: `Grade: ${grade}`, key: 'grade' });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]" data-testid="tickets-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-200">
        <div className="flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              className="text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg"
              onClick={() => navigate('/')}
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-1.5" />
              <span className="text-xs">Dashboard</span>
            </Button>

            <div className="h-6 w-px bg-slate-200" />

            <img src={LOGO_URL} alt="Gamer Packaging" className="h-8 object-contain logo-container" />

            <h1 className="font-heading text-sm font-bold tracking-wide text-slate-800" data-testid="tickets-page-title">
              Ticket Browser
            </h1>
          </div>

          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchTickets()}
                placeholder="Search tickets..."
                className="bg-white border border-slate-200 rounded-lg pl-8 pr-3 py-1.5 text-xs text-slate-700 placeholder-slate-400 focus:outline-none focus:border-[#B22234]/50 w-48 md:w-64 transition-colors"
                data-testid="ticket-search-input"
              />
            </div>
            <span className="text-xs font-semibold text-[#B22234]" data-testid="ticket-count">
              {total} tickets
            </span>
          </div>
        </div>
      </header>

      {/* Active Filters */}
      {activeFilters.length > 0 && (
        <div className="px-6 py-2.5 bg-slate-50 border-b border-slate-200 flex items-center gap-2 flex-wrap" data-testid="active-filters">
          <span className="text-[11px] text-slate-400">Filters:</span>
          {activeFilters.map((f) => (
            <span key={f.key} className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-md bg-[#B22234]/10 text-[#B22234] border border-[#B22234]/20 capitalize font-medium">
              {f.label}
            </span>
          ))}
          <button
            className="text-[11px] text-slate-400 hover:text-[#B22234] transition-colors ml-2 font-medium"
            onClick={() => navigate('/tickets')}
            data-testid="clear-filters-btn"
          >
            Clear all
          </button>
        </div>
      )}

      {/* Ticket Table */}
      <div className="p-6 max-w-[1600px] mx-auto">
        <div className="corp-card overflow-hidden">
          <ScrollArea className="w-full">
            <Table data-testid="tickets-table">
              <TableHeader>
                <TableRow className="border-b border-slate-100 hover:bg-transparent">
                  <TableHead className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider w-[60px]">#</TableHead>
                  <TableHead className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Summary</TableHead>
                  <TableHead className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider w-[100px]">Status</TableHead>
                  <TableHead className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider w-[80px]">Priority</TableHead>
                  <TableHead className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider w-[140px]">Assigned To</TableHead>
                  <TableHead className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider w-[120px]">Category</TableHead>
                  <TableHead className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider w-[100px]">Created</TableHead>
                  <TableHead className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider w-[40px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 10 }).map((_, i) => (
                    <TableRow key={i} className="border-b border-slate-50">
                      <TableCell colSpan={8} className="h-10">
                        <div className="h-3 bg-slate-100 rounded data-pulse w-full" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : tickets.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-12 text-slate-400 text-sm">
                      No tickets found
                    </TableCell>
                  </TableRow>
                ) : (
                  tickets.map((ticket) => (
                    <TableRow
                      key={ticket.ticket_number}
                      className="border-b border-slate-50 ticket-row cursor-pointer"
                      onClick={() => setSelectedTicket(selectedTicket?.ticket_number === ticket.ticket_number ? null : ticket)}
                      data-testid={`ticket-row-${ticket.ticket_number}`}
                    >
                      <TableCell className="text-sm font-semibold text-[#B22234]">
                        {ticket.ticket_number}
                      </TableCell>
                      <TableCell className="text-sm text-slate-700 max-w-[300px] truncate font-medium">
                        {ticket.summary}
                      </TableCell>
                      <TableCell>
                        <span className={`text-[10px] px-2 py-0.5 rounded-md uppercase tracking-wider font-medium status-${ticket.status}`}>
                          {ticket.status}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className={`text-[10px] px-2 py-0.5 rounded-md uppercase tracking-wider font-medium priority-${ticket.priority}`}>
                          {ticket.priority}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500 truncate">
                        {ticket.assigned_to || '-'}
                      </TableCell>
                      <TableCell className="text-xs text-slate-400 truncate">
                        {ticket.category || '-'}
                      </TableCell>
                      <TableCell className="text-xs text-slate-400">
                        {formatDate(ticket.created_on)}
                      </TableCell>
                      <TableCell>
                        {ticket.link_to_ticket && (
                          <a
                            href={ticket.link_to_ticket}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-slate-300 hover:text-[#B22234] transition-colors"
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`ticket-link-${ticket.ticket_number}`}
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </ScrollArea>

          {/* Ticket Detail Expansion */}
          {selectedTicket && (
            <div className="border-t border-slate-100 bg-slate-50 p-5 animate-fade-slide-up" data-testid="ticket-detail-panel">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <span className="text-sm font-bold text-[#B22234]">#{selectedTicket.ticket_number}</span>
                  <h3 className="text-sm text-slate-800 font-semibold mt-1">{selectedTicket.summary}</h3>
                </div>
                <div className="flex gap-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded-md uppercase tracking-wider font-medium status-${selectedTicket.status}`}>
                    {selectedTicket.status}
                  </span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-md uppercase tracking-wider font-medium priority-${selectedTicket.priority}`}>
                    {selectedTicket.priority}
                  </span>
                </div>
              </div>
              {selectedTicket.description && (
                <p className="text-sm text-slate-600 leading-relaxed mb-4 max-h-32 overflow-y-auto bg-white rounded-lg p-3 border border-slate-100">
                  {selectedTicket.description}
                </p>
              )}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-slate-400 text-xs block mb-0.5">Assigned To</span>
                  <span className="text-slate-700 font-medium">{selectedTicket.assigned_to || '-'}</span>
                </div>
                <div>
                  <span className="text-slate-400 text-xs block mb-0.5">Category</span>
                  <span className="text-slate-700 font-medium">{selectedTicket.category || '-'}</span>
                </div>
                <div>
                  <span className="text-slate-400 text-xs block mb-0.5">Created</span>
                  <span className="text-slate-700 font-medium">{formatDate(selectedTicket.created_on)}</span>
                </div>
                <div>
                  <span className="text-slate-400 text-xs block mb-0.5">Created By</span>
                  <span className="text-slate-700 font-medium">{selectedTicket.created_by || '-'}</span>
                </div>
                <div>
                  <span className="text-slate-400 text-xs block mb-0.5">Closed</span>
                  <span className="text-slate-700 font-medium">{formatDate(selectedTicket.closed_on)}</span>
                </div>
                <div>
                  <span className="text-slate-400 text-xs block mb-0.5">Time Spent</span>
                  <span className="text-slate-700 font-medium">{selectedTicket.time_spent > 0 ? `${selectedTicket.time_spent}m` : '-'}</span>
                </div>
                <div>
                  <span className="text-slate-400 text-xs block mb-0.5">Time To Resolve</span>
                  <span className="text-slate-700 font-medium">{selectedTicket.time_to_resolve > 0 ? `${selectedTicket.time_to_resolve}m` : '-'}</span>
                </div>
                {selectedTicket.link_to_ticket && (
                  <div>
                    <span className="text-slate-400 text-xs block mb-0.5">Spiceworks Link</span>
                    <a href={selectedTicket.link_to_ticket} target="_blank" rel="noopener noreferrer" className="text-[#B22234] hover:underline text-sm font-medium">
                      Open in Spiceworks
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100" data-testid="pagination">
              <span className="text-xs text-slate-400">
                Page {page} of {pages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="border-slate-200 bg-white hover:bg-slate-50 text-slate-600 rounded-lg h-7 px-2"
                  disabled={page <= 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  data-testid="prev-page-btn"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-slate-200 bg-white hover:bg-slate-50 text-slate-600 rounded-lg h-7 px-2"
                  disabled={page >= pages}
                  onClick={() => setPage(p => Math.min(pages, p + 1))}
                  data-testid="next-page-btn"
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
